/**
 * Live-binding holder for the lazily-loaded slate editor lib (slate + slate-react
 * + slate-history).
 *
 * ── Why a holder instead of `import … from "slate"` ────────────────────────────
 * The slate editor tree (tasks/extraData/editor/* + ConversationEditor + the
 * reminders editor) is statically bundled in app.js (it MUST be — a content
 * script can't webpack-async-import it: WhatsApp Web's CSP blocks
 * chrome-extension:// <script> chunks in the page's MAIN world). If those
 * components imported slate directly, slate would be pinned in app.js forever.
 *
 * Instead they import the primitives they use (Editor / Transforms / Slate /
 * Editable / withReact / withHistory / useSlate / …) from THIS module, whose
 * exports are `let` bindings. `loadSlateLib()` (src/extension/utils/lazy-lib.ts)
 * loads the slate lib on demand — from the native WAR bundle dist/lazy/slate.js
 * (CSP-safe isolated-world import, sharing app.js's React via the __BT_REACT__
 * shim bridge) or, while the fallback flag is on, from an eager app.js copy —
 * and calls `setSlateRuntime(...)` to populate these bindings. Webpack compiles
 * `export let` to live getters, so every consumer sees the populated values.
 *
 * The React.lazy boundary in
 * src/common/components/tasks/extraData/lazy-slate.tsx awaits loadSlateLib()
 * BEFORE it renders any slate component, so the bindings are always populated by
 * first render. Do NOT reference these at module scope in a consumer — only at
 * render / event time.
 *
 * ── Identity guarantee (instanceof / WeakMap safety) ───────────────────────────
 * loadSlateLib() is memoized (once()), so exactly ONE slate + slate-react copy
 * ever populates this holder per document (whichever the first load resolved
 * with — WAR or fallback). Because every editor sub-component reads Slate /
 * Editable / useSlate / ReactEditor from THIS single holder, slate-react's
 * module-level WeakMaps and its React context have one consistent identity —
 * the same rule the lexical/calendar splits follow. Mixing two slate copies
 * would break Editor.isEditor / Element.isElement checks and DOM<->node lookups.
 *
 * Typed as `any` on purpose (mirrors lazy-lib.ts): slate's dual type/value names
 * (Editor, Element, Range, Node) can't be re-exported through a runtime `let`
 * without losing the interface meaning, and the editor code is already heavily
 * `any`. Type-only names (Descendant, CustomEditor, RenderElementProps, …) keep
 * importing straight from "slate" / "slate-react" via `import type` — those are
 * erased at build time and add no weight to app.js.
 */

/* eslint-disable import/no-mutable-exports */

// The runtime VALUE bindings are typed with `typeof import(...)` so call sites
// keep their real slate typing (e.g. Editor.nodes' match callback is inferred,
// avoiding noImplicitAny). `typeof import(...)` is type-space only — erased at
// build, so it pulls NO slate into app.js.
//
// The dual-meaning names (Editor / Element / Range / Node) also need a matching
// TYPE export so consumers can write `editor: Editor` / `Range | null`, mirroring
// slate's own value+type merged exports. Declaration merging lets a `let` value
// and a `type` share one name.

// slate (core)
export let createEditor: typeof import("slate").createEditor;
export let Editor: typeof import("slate").Editor;
export let Element: typeof import("slate").Element;
export let Transforms: typeof import("slate").Transforms;
export let Range: typeof import("slate").Range;
export let Node: typeof import("slate").Node;

export type Editor = import("slate").Editor;
export type Element = import("slate").Element;
export type Range = import("slate").Range;
export type Node = import("slate").Node;
export type Text = import("slate").Text;
export type Descendant = import("slate").Descendant;

// slate-react
export let Slate: typeof import("slate-react").Slate;
export let Editable: typeof import("slate-react").Editable;
export let withReact: typeof import("slate-react").withReact;
export let ReactEditor: typeof import("slate-react").ReactEditor;
export let useSlate: typeof import("slate-react").useSlate;
export let useSlateStatic: typeof import("slate-react").useSlateStatic;
export let useFocused: typeof import("slate-react").useFocused;
export let useSelected: typeof import("slate-react").useSelected;
export let useReadOnly: typeof import("slate-react").useReadOnly;

// slate-history
export let withHistory: typeof import("slate-history").withHistory;

let installed = false;

/**
 * Populate the live bindings from a loaded slate lib module (the WAR bundle
 * dist/lazy/slate.js re-exports all these names; the eager fallback passes the
 * merged slate + slate-react + slate-history namespace). Idempotent.
 */
export function setSlateRuntime(mod: any): void {
  if (installed || !mod) return;
  createEditor = mod.createEditor;
  Editor = mod.Editor;
  Element = mod.Element;
  Transforms = mod.Transforms;
  Range = mod.Range;
  Node = mod.Node;
  Slate = mod.Slate;
  Editable = mod.Editable;
  withReact = mod.withReact;
  ReactEditor = mod.ReactEditor;
  useSlate = mod.useSlate;
  useSlateStatic = mod.useSlateStatic;
  useFocused = mod.useFocused;
  useSelected = mod.useSelected;
  useReadOnly = mod.useReadOnly;
  withHistory = mod.withHistory;
  installed = true;
}

/** True once the slate lib has been loaded and the bindings are live. */
export function isSlateRuntimeReady(): boolean {
  return installed;
}
